import React, { useState, useEffect, useRef } from 'react';
import { Card, Button, Spinner, Form, Modal, Row, Col } from 'react-bootstrap';
import { FiUpload, FiDownload, FiTrash2, FiPaperclip } from 'react-icons/fi';
import { toast } from 'react-toastify';
import attachmentsService, { Attachment } from '../services/attachments.service';

interface Props {
  entityType: 'case' | 'hearing' | 'task';
  entityId: number;
  readOnly?: boolean;
}

const DOCUMENT_TYPES = [
  'Demanda', 'Contestación', 'Poder', 'Auto', 'Providencia', 'Sentencia',
  'Acuerdo / Conciliación', 'Liquidación', 'Prueba documental', 'Recurso',
  'Notificación', 'Certificado', 'Otro'
];

const AttachmentPanel: React.FC<Props> = ({ entityType, entityId, readOnly = false }) => {
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [description, setDescription] = useState('');
  const [documentType, setDocumentType] = useState('');
  const [downloadingId, setDownloadingId] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (entityId) load();
  }, [entityType, entityId]);

  const load = async () => {
    try {
      setLoading(true);
      const data = await attachmentsService.list(entityType, entityId);
      setAttachments(data);
    } catch {
      // silencioso
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setSelectedFile(file);
    setShowModal(true);
    e.target.value = '';
  };

  const handleUpload = async () => {
    if (!selectedFile) return;
    try {
      setUploading(true);
      await attachmentsService.upload(entityType, entityId, selectedFile, {
        description: description || undefined,
        document_type: documentType || undefined
      });
      toast.success('Archivo subido correctamente');
      setShowModal(false);
      setSelectedFile(null);
      setDescription('');
      setDocumentType('');
      await load();
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Error al subir el archivo');
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (att: Attachment) => {
    try {
      setDownloadingId(att.id);
      await attachmentsService.download(att.id, att.original_filename);
    } catch {
      toast.error('Error al descargar el archivo');
    } finally {
      setDownloadingId(null);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('¿Eliminar este adjunto?')) return;
    try {
      await attachmentsService.remove(id);
      toast.success('Adjunto eliminado');
      setAttachments(prev => prev.filter(a => a.id !== id));
    } catch {
      toast.error('Error al eliminar el adjunto');
    }
  };

  const formatDate = (d: string) =>
    new Date(d).toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' });

  return (
    <>
      <Card className="mb-3">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <span style={{ fontWeight: 600 }}>
            <FiPaperclip className="me-2" />
            Documentos adjuntos
            {attachments.length > 0 && (
              <span className="badge bg-secondary ms-2">{attachments.length}</span>
            )}
          </span>
          {!readOnly && (
            <>
              <Button
                size="sm"
                variant="outline-primary"
                onClick={() => fileInputRef.current?.click()}
              >
                <FiUpload className="me-1" /> Subir documento
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                hidden
                accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.txt,.zip"
                onChange={handleFileSelect}
              />
            </>
          )}
        </Card.Header>

        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center py-3">
              <Spinner animation="border" size="sm" variant="primary" />
            </div>
          ) : attachments.length === 0 ? (
            <div className="text-center text-muted py-4" style={{ fontSize: 13 }}>
              No hay documentos adjuntos
            </div>
          ) : (
            <div style={{ maxHeight: 320, overflowY: 'auto' }}>
              {attachments.map(att => (
                <div
                  key={att.id}
                  className="d-flex align-items-center px-3 py-2 border-bottom"
                  style={{ gap: 10 }}
                >
                  <span style={{ fontSize: 20 }}>{attachmentsService.getIcon(att.mime_type, att.original_filename)}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 500, fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {att.original_filename}
                    </div>
                    <div style={{ fontSize: 11, color: '#6c757d' }}>
                      {att.document_type && <span className="badge bg-light text-dark border me-1">{att.document_type}</span>}
                      {attachmentsService.formatSize(att.file_size)} · {formatDate(att.uploaded_at)}
                      {att.uploaded_by_name && ` · ${att.uploaded_by_name}`}
                    </div>
                    {att.description && (
                      <div style={{ fontSize: 11, color: '#6c757d', fontStyle: 'italic' }}>{att.description}</div>
                    )}
                  </div>
                  <div className="d-flex gap-1 flex-shrink-0">
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      title="Descargar"
                      disabled={downloadingId === att.id}
                      onClick={() => handleDownload(att)}
                    >
                      {downloadingId === att.id
                        ? <Spinner animation="border" size="sm" />
                        : <FiDownload />
                      }
                    </Button>
                    {!readOnly && (
                      <Button
                        size="sm"
                        variant="outline-danger"
                        title="Eliminar"
                        onClick={() => handleDelete(att.id)}
                      >
                        <FiTrash2 />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Modal de metadatos del archivo */}
      <Modal show={showModal} onHide={() => { setShowModal(false); setSelectedFile(null); }} centered>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: 16 }}>Subir documento</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedFile && (
            <div className="p-2 mb-3 bg-light rounded d-flex align-items-center gap-2">
              <span style={{ fontSize: 22 }}>{attachmentsService.getIcon(selectedFile.type, selectedFile.name)}</span>
              <div>
                <div style={{ fontWeight: 500 }}>{selectedFile.name}</div>
                <small className="text-muted">{attachmentsService.formatSize(selectedFile.size)}</small>
              </div>
            </div>
          )}
          <Row className="g-3">
            <Col md={12}>
              <Form.Group>
                <Form.Label>Tipo de documento</Form.Label>
                <Form.Select value={documentType} onChange={e => setDocumentType(e.target.value)}>
                  <option value="">Seleccionar...</option>
                  {DOCUMENT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={12}>
              <Form.Group>
                <Form.Label>Descripción (opcional)</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  placeholder="Breve descripción del documento..."
                />
              </Form.Group>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => { setShowModal(false); setSelectedFile(null); }}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={handleUpload} disabled={uploading}>
            {uploading ? <><Spinner animation="border" size="sm" className="me-2" />Subiendo...</> : <><FiUpload className="me-1" />Subir</>}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default AttachmentPanel;
